/**
 * Mobile Progress Note Page
 * Allows creating progress notes offline with auto-sync
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Save, Send, Smile, Meh, Frown, Clock, CheckCircle, Camera } from 'lucide-react';
import { queueOfflineAction, ACTION_TYPES, ACTION_STATUS, getQueueStats } from '@/lib/offline/actionQueue';
import { syncAllActions, isOnline } from '@/lib/offline/syncEngine';
import { SyncStatusBadge, OfflineIndicator, SYNC_STATUS } from '@/components/mobile/SyncStatusBadge';
import MobileAttachmentCapture from '@/components/mobile/MobileAttachmentCapture';
import { ATTACHMENT_ENTITY_TYPES } from '@/lib/offline/attachmentStorage';

const NOTE_TYPES = [
  { value: 'GENERAL', label: 'General Note' },
  { value: 'DAILY_SUMMARY', label: 'Daily Summary' },
  { value: 'ACTIVITY', label: 'Activity Record' },
  { value: 'HEALTH', label: 'Health Observation' },
  { value: 'BEHAVIOR', label: 'Behavior Note' },
  { value: 'COMMUNICATION', label: 'Communication' },
];

const MOOD_OPTIONS = [
  { value: 'HAPPY', label: 'Happy', icon: Smile, color: 'text-green-500' },
  { value: 'NEUTRAL', label: 'Neutral', icon: Meh, color: 'text-amber-500' },
  { value: 'SAD', label: 'Sad', icon: Frown, color: 'text-red-500' },
];

const ACTIVITY_OPTIONS = [
  'Personal Care', 'Meal Preparation', 'Medication Support', 'Community Access',
  'Exercise', 'Social Activity', 'Domestic Tasks', 'Transport', 'Other'
];

export default function MobileProgressNotePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('participantId');
  const shiftId = searchParams.get('shiftId');

  const [online, setOnline] = useState(isOnline());
  const [saving, setSaving] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [form, setForm] = useState({
    participantId: participantId || '',
    shiftId: shiftId || '',
    noteType: 'GENERAL',
    content: '',
    mood: '',
    activities: [],
  });
  const [attachments, setAttachments] = useState([]);

  // Online/offline listener
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch participants for dropdown
  useEffect(() => {
    if (online) {
      api.get('/participants', { params: { pageSize: 100 } })
        .then(res => setParticipants(res.data.items || []))
        .catch(() => {});
    }
  }, [online]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleActivity = (activity) => {
    setForm(prev => ({
      ...prev,
      activities: prev.activities.includes(activity)
        ? prev.activities.filter(a => a !== activity)
        : [...prev.activities, activity]
    }));
  };

  const saveDraft = async () => {
    if (!form.content.trim()) {
      toast.error('Please enter note content');
      return;
    }

    setSaving(true);
    setSyncStatus(SYNC_STATUS.PENDING);

    try {
      // Queue the action for offline sync
      const action = await queueOfflineAction(ACTION_TYPES.PROGRESS_NOTE, {
        participantId: form.participantId,
        shiftId: form.shiftId,
        noteType: form.noteType,
        content: form.content,
        mood: form.mood || null,
        activities: form.activities,
        timestamp: new Date().toISOString(),
        attachmentIds: attachments.map(a => a.id),
      });

      toast.success('Note saved locally', {
        description: online ? 'Syncing...' : 'Will sync when online',
      });

      // If online, try to sync immediately
      if (online) {
        setSyncStatus(SYNC_STATUS.SYNCING);
        try {
          const results = await syncAllActions();
          if (results.synced > 0) {
            setSyncStatus(SYNC_STATUS.SYNCED);
            toast.success('Note synced successfully');
            setTimeout(() => navigate(-1), 1000);
          } else if (results.failed > 0) {
            setSyncStatus(SYNC_STATUS.FAILED);
          }
        } catch (err) {
          setSyncStatus(SYNC_STATUS.FAILED);
        }
      } else {
        // Will sync later when online
        setTimeout(() => navigate(-1), 1500);
      }
    } catch (err) {
      toast.error('Failed to save note');
      setSyncStatus(SYNC_STATUS.FAILED);
    } finally {
      setSaving(false);
    }
  };

  const handleRetrySync = async () => {
    setSyncStatus(SYNC_STATUS.SYNCING);
    try {
      const results = await syncAllActions();
      if (results.synced > 0) {
        setSyncStatus(SYNC_STATUS.SYNCED);
        toast.success('Synced successfully');
      } else {
        setSyncStatus(SYNC_STATUS.FAILED);
      }
    } catch (err) {
      setSyncStatus(SYNC_STATUS.FAILED);
      toast.error('Sync failed');
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="mobile-progress-note-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">New Progress Note</h1>
            <p className="text-xs text-muted-foreground">Record participant progress</p>
          </div>
        </div>
      </div>

      {/* Offline/Sync Status */}
      <div className="px-4 pt-4 space-y-2">
        <OfflineIndicator isOffline={!online} />
        {syncStatus && (
          <SyncStatusBadge 
            status={syncStatus} 
            onRetry={syncStatus === SYNC_STATUS.FAILED ? handleRetrySync : undefined}
          />
        )}
      </div>

      {/* Form */}
      <div className="p-4 space-y-4">
        {/* Participant Selection */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="participant">Participant</Label>
              <Select 
                value={form.participantId || undefined} 
                onValueChange={(v) => handleChange('participantId', v)}
              >
                <SelectTrigger id="participant" data-testid="participant-select">
                  <SelectValue placeholder="Select participant" />
                </SelectTrigger>
                <SelectContent>
                  {participants.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.first_name} {p.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="noteType">Note Type</Label>
              <Select 
                value={form.noteType} 
                onValueChange={(v) => handleChange('noteType', v)}
              >
                <SelectTrigger id="noteType" data-testid="note-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NOTE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Mood Selection */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Participant Mood</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex gap-2">
              {MOOD_OPTIONS.map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleChange('mood', form.mood === value ? '' : value)}
                  className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                    form.mood === value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-transparent bg-muted/50 hover:bg-muted'
                  }`}
                  data-testid={`mood-${value.toLowerCase()}`}
                >
                  <Icon className={`h-6 w-6 ${color}`} />
                  <span className="text-xs font-medium">{label}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Activities */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Activities (Optional)</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex flex-wrap gap-2">
              {ACTIVITY_OPTIONS.map(activity => (
                <button
                  key={activity}
                  type="button"
                  onClick={() => toggleActivity(activity)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    form.activities.includes(activity)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                  data-testid={`activity-${activity.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {activity}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Note Content */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Note Content *</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Textarea
              id="content"
              placeholder="Enter your progress note here..."
              value={form.content}
              onChange={(e) => handleChange('content', e.target.value)}
              rows={6}
              className="resize-none"
              data-testid="note-content"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {form.content.length} characters
            </p>
          </CardContent>
        </Card>

        {/* Attachments */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Photos / Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <MobileAttachmentCapture
              entityType={ATTACHMENT_ENTITY_TYPES.PROGRESS_NOTE}
              entityId={null}
              maxAttachments={5}
              acceptedTypes="image/*,application/pdf"
              onAttachmentsChange={setAttachments}
              initialAttachments={attachments}
            />
          </CardContent>
        </Card>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 safe-area-inset-bottom">
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button 
            className="flex-1"
            onClick={saveDraft}
            disabled={saving || !form.content.trim()}
            data-testid="save-note-btn"
          >
            {saving ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {online ? 'Save & Sync' : 'Save Draft'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
